/* UnlockedGridGroupRowRenderer.js */

'use strict';

const _ = require('lodash');
const GridCell = require('../../cell/GridCellComponent');
const GridRowRenderer = require('../GridRowRenderer');
const EmptyColumnRendererComponent = require('../../column_renderer/empty/EmptyColumnComponent');

class UnlockedGridGroupRowRenderer extends GridRowRenderer {
    constructor(model) {
        super(model);

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _createRootElement() {
        const rootElem = document.createElement('unlocked-grid-row');
        rootElem.classList.add('group-header-row');
        return rootElem;
    }

    _registerListeners() {
        super._registerListeners();
        this._model.getGrid().on('GridResized', (this._onGridResized = this._onGridResized.bind(this)));
        this._model.getGrid().on('UserScrolled', (this._onGridUserScrolled = this._onGridUserScrolled.bind(this)));
        this._model.on('Expand', this._onExpand = this._onExpand.bind(this));
    }

    _unregisterListeners() {
        super._unregisterListeners();
        this._model.getGrid().removeListener('GridResized', this._onGridResized);
        this._model.getGrid().removeListener('UserScrolled', this._onGridUserScrolled);
        this._model.removeListener('Expand', this._onExpand);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._setExpanded(this._model.getExpanded());
        return super.render();
    }

    shutdown() {
        if (!_.isUndefined(this._displayAdjustTimeout)) {
            clearTimeout(this._displayAdjustTimeout);
        }
        this._displayAdjustTimeout = undefined;
        this._gridBodyWidth = undefined;
        this._gridBodyScrollLeft = undefined;

        return super.shutdown();
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Triggered by the 'UserResized' event for each column.  See model for event binding note.
     * @param columnData.column {String} - The id of the column that has been resized
     * @param columnData.width {Number} - The width in pixels of the column
     * @param columnData.event {Event} - The mouse event that triggered the resize
     * @private
     */
    _onColumnWidth(columnData) {
        super._onColumnWidth(columnData);
        this._updateRowWidth();
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Triggered when the grid has been resized
     * @private
     */
    _onGridResized(/*eventData*/) {
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Triggered when the grid has been scrolled by the user
     * @param eventData.scrollTop {Number} - The pixel value of the distance the grid body has been scrolled from the top
     * @param eventData.scrollLeft {Number} - The pixel value of the distance the grid body has been scrolled from the left
     * @param eventData.event {Event} - The DOM scroll event
     * @private
     */
    _onGridUserScrolled(eventData) {
        this._gridBodyScrollLeft = eventData.scrollLeft;
        this._adjustVirtualRenderingDisplay();
    }

    _onExpand(state) {
        this._setExpanded(state);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getColumns() {
        return this._model.getUnlockedColumns();
    }

    /**
     * Build the row display from the entity
     * @param entity {object} New entity that contains data for the current row
     * @private
     */
    _setEntity(entity) {
        for (let cell in this._cells) {
            if (this._cells.hasOwnProperty(cell)) {
                this._cells[cell].removeAllListeners('CellAction');
                this._cells[cell].shutdown();
            }
        }
        this._cells = {};
        let columns = this._getColumns(),
            columnComponents = this._model.getColumnComponents();
        columns.forEach((columnKey) => {
            const colComponent = columnComponents[columnKey];
            const cellParams = {
                id: columnKey,
                column: colComponent,
                columnRendererComponent: EmptyColumnRendererComponent,
                shown: colComponent ? colComponent.getShown() : undefined
            };
            if (colComponent.isGroupOnlyColumn || colComponent.isCombinedColumn) {

                Object.assign(cellParams, {
                    entityColumn: colComponent ? colComponent.getEntityColumn() : undefined,
                    entity: entity,
                    context: colComponent ? colComponent.getContext() : undefined,
                    path: colComponent ? colComponent.getPath() : undefined,
                    columnRendererComponent: colComponent.getColumnRendererComponent() || EmptyColumnRendererComponent,
                });
            }
            let cell = new GridCell(cellParams);
            cell.addListener('CellAction', this._onCellAction = this._onCellAction.bind(this));
            this._rootElem.appendChild(cell.renderer.rootElement);
            this._cells[columnKey] = cell;
        });
        this._updateRowWidth();
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Show/hide cells if they are not displaying in the grid bodies scrollable area.  Use a buffer
     * of 20 rems on either side of the scrollable area as a boundary for the show/hide action to occur.
     * @private
     */
    _adjustVirtualRenderingDisplay() {
        if (_.isUndefined(this._displayAdjustTimeout)) {
            this._displayAdjustTimeout = setTimeout(() => {
                this._captureGridBodyWidth();
                if (this._gridBodyWidth !== 0) {
                    let unlockedColumns = this._model.getUnlockedColumns(),
                        offScreenBufferWidth = 200,
                        rowWidthTotal = 0,
                        paddingLeft = 0,
                        gridBodyScrollLeft = _.isUndefined(this._gridBodyScrollLeft) ? 0 : this._gridBodyScrollLeft,
                        columnComponents = this._model.getColumnComponents();
                    unlockedColumns.forEach(columnKey => {
                        if (columnComponents[columnKey].getShown()) {
                            let cell = this._cells[columnKey],
                                cellWidth = cell.getWidth(),
                                offScreenLeft = cell.getOffScreenLeft();
                            // Off screen to the left
                            if (gridBodyScrollLeft > rowWidthTotal + cellWidth + offScreenBufferWidth) {
                                cell.setShown(false);
                                cell.setOffScreenLeft(true);
                                paddingLeft += cell.getWidth();
                                // On screen
                            } else {
                                if (offScreenLeft === true) {
                                    paddingLeft -= cell.getWidth();
                                }
                                cell.setShown(true);
                                cell.setOffScreenLeft(false);
                            }
                            rowWidthTotal += cellWidth;
                        }
                    });
                    this._rootElem.style.paddingLeft = paddingLeft / 10 + 'rem';
                }
                this._displayAdjustTimeout = undefined;
            }, this._model.getTimeoutDuration());
        }
    }

    /**
     * Rebuild the row with the new column order
     * @param lockedColumns {Array} - Array of Strings (column ids) representing the order of the locked columns
     * @private
     */
    _setLockedColumns(/*lockedColumns*/) {
        // console.info('SET UNLOCKED LOCKED COLS');
        let unlockedColumns = this._model.getUnlockedColumns(),
            columnComponents = this._model.getColumnComponents(),
            cellKeys = Object.keys(this._cells),
            cellsToRemove = _.difference(cellKeys, unlockedColumns),
            cellsToAdd = _.difference(unlockedColumns, cellKeys);
        cellsToRemove.forEach(columnKey => {
            this._cells[columnKey].removeAllListeners('CellAction');
            this._cells[columnKey].shutdown();
            delete this._cells[columnKey];
        });
        cellsToAdd.forEach(columnKey => {
            let nextCellIndex = unlockedColumns.indexOf(columnKey) + 1,
                nextCellKey = nextCellIndex <= unlockedColumns.length - 1 ? unlockedColumns[nextCellIndex] : undefined,
                nextCellRenderer =
                    (!_.isUndefined(nextCellKey) &&
                        this._cells[nextCellKey] &&
                        this._cells[nextCellKey].renderer.rootElement) ||
                    null;
            let cell = new GridCell({
                id: columnKey,
                column: columnComponents[columnKey],
                columnRendererComponent: EmptyColumnRendererComponent,
                shown: columnComponents[columnKey].getShown(),
            });
            cell.addListener('CellAction', (this._onCellAction = this._onCellAction.bind(this)));
            this._cells[columnKey] = cell;
            this._rootElem.insertBefore(cell.renderer.rootElement, nextCellRenderer);
        });
        this._updateRowWidth();
        this._adjustVirtualRenderingDisplay();
    }

    /**
     * Set the row width
     * @param newVal {Number} - Number in pixels of the width of the row
     * @private
     */
    _setRowWidth(newVal) {
        this._rootElem.style.width = newVal / 10 + 'rem';
    }

    /**
     * Capture the grid body width to use in virtual rendering calculations
     * @private
     */
    _captureGridBodyWidth() {
        let scrollableGridBody = this._rootElem.closest('grid-body-container');
        if (scrollableGridBody !== null) {
            this._gridBodyWidth = scrollableGridBody.clientWidth;
        }
    }

    /**
     * Adjust row width
     * @private
     */
    _updateRowWidth() {
        let unlockedColumns = this._model.getUnlockedColumns(),
            columnComponents = this._model.getColumnComponents(),
            rowWidth = 0;
        unlockedColumns.forEach(columnKey => {
            if (columnComponents[columnKey].getShown()) {
                rowWidth += this._cells[columnKey].getWidth();
            }
        });
        this._setRowWidth(rowWidth);
    }

    _setExpanded(state) {
        this._rootElem.classList.toggle('expanded', state);
        this._rootElem.classList.toggle('collapsed', !state);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = UnlockedGridGroupRowRenderer;
